import { useState, useEffect, useRef } from 'react'
import type { CSSProperties } from 'react'
import { CheckIcon, XMarkIcon, GlobeAltIcon, TrashIcon } from '@heroicons/react/24/outline'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSpotify, faYoutube, faInstagram, faSoundcloud,
  faWikipediaW, faTiktok, faBandcamp, faApple,
} from '@fortawesome/free-brands-svg-icons'
import type { Candidate } from '../api'


// ── Platform detection ────────────────────────────────────────────────────

type Platform = 'spotify' | 'youtube' | 'instagram' | 'tiktok' | 'soundcloud' | 'bandcamp' | 'apple_music' | 'wikipedia' | 'website'

function detectPlatform(url: string): Platform {
  if (/open\.spotify\.com\/artist\//.test(url)) return 'spotify'
  if (/youtube\.com\/@|youtube\.com\/c\/|youtube\.com\/channel\/|youtube\.com\/user\//.test(url)) return 'youtube'
  if (/instagram\.com\//.test(url)) return 'instagram'
  if (/tiktok\.com\/@/.test(url)) return 'tiktok'
  if (/soundcloud\.com\//.test(url)) return 'soundcloud'
  if (/\.bandcamp\.com/.test(url)) return 'bandcamp'
  if (/music\.apple\.com/.test(url)) return 'apple_music'
  if (/en\.wikipedia\.org\/wiki\//.test(url)) return 'wikipedia'
  return 'website'
}

function parseLinks(raw: string): string[] {
  return raw.split('\n').map(u => u.trim()).filter(Boolean)
}

// ── Embed selection ───────────────────────────────────────────────────────
// Artists:      Spotify > Apple Music > Bandcamp (blocks SC) > SoundCloud
// Record Labels: streaming embed if present, otherwise featured Instagram link
// All others:   no embed

type EmbedInfo =
  | { type: 'spotify';     spotifyId: string }
  | { type: 'apple_music'; embedUrl: string  }
  | { type: 'soundcloud';  profileUrl: string }
  | { type: 'instagram';   url: string        }

function isLabel(entityType: string) {
  return /label/i.test(entityType)
}

function isArtist(entityType: string) {
  return /artist/i.test(entityType)
}

function findStreamingEmbed(links: string[]): EmbedInfo | null {
  // Spotify
  for (const url of links) {
    const m = url.match(/open\.spotify\.com\/artist\/([A-Za-z0-9]+)/)
    if (m) return { type: 'spotify', spotifyId: m[1] }
  }
  // Apple Music
  for (const url of links) {
    if (/music\.apple\.com\/\w+\/artist\//.test(url)) {
      return { type: 'apple_music', embedUrl: url.replace('//music.apple.com', '//embed.music.apple.com') }
    }
  }
  // Bandcamp present but not embeddable → blocks SoundCloud
  if (links.some(u => /\.bandcamp\.com/.test(u))) return null
  // SoundCloud
  for (const url of links) {
    if (url.includes('soundcloud.com/')) {
      const m = url.match(/https?:\/\/(?:www\.)?soundcloud\.com\/([\w-]+)/)
      if (m) return { type: 'soundcloud', profileUrl: `https://soundcloud.com/${m[1]}` }
    }
  }
  return null
}

function findEmbed(links: string[], entityType: string): EmbedInfo | null {
  if (isArtist(entityType)) {
    return findStreamingEmbed(links)
  }
  if (isLabel(entityType)) {
    const streaming = findStreamingEmbed(links)
    if (streaming) return streaming
    // Fall back to featured Instagram link
    for (const url of links) {
      if (/instagram\.com\//.test(url)) return { type: 'instagram', url }
    }
    return null
  }
  // Venues, managers, agents, etc. — no embed
  return null
}

// ── Platform icon + label ─────────────────────────────────────────────────

function platformIcon(platform: Platform) {
  switch (platform) {
    case 'spotify':     return <FontAwesomeIcon icon={faSpotify} />
    case 'youtube':     return <FontAwesomeIcon icon={faYoutube} />
    case 'instagram':   return <FontAwesomeIcon icon={faInstagram} />
    case 'tiktok':      return <FontAwesomeIcon icon={faTiktok} />
    case 'soundcloud':  return <FontAwesomeIcon icon={faSoundcloud} />
    case 'bandcamp':    return <FontAwesomeIcon icon={faBandcamp} />
    case 'apple_music': return <FontAwesomeIcon icon={faApple} />
    case 'wikipedia':   return <FontAwesomeIcon icon={faWikipediaW} />
    default:            return <GlobeAltIcon style={{ width: 12 }} />
  }
}

function platformLabel(platform: Platform, url: string): string {
  switch (platform) {
    case 'spotify':     return 'Spotify'
    case 'youtube':     return 'YouTube'
    case 'instagram': {
      const m = url.match(/instagram\.com\/([\w.]+)/)
      return m ? `@${m[1]}` : 'Instagram'
    }
    case 'tiktok': {
      const m = url.match(/tiktok\.com\/@([\w.]+)/)
      return m ? `@${m[1]}` : 'TikTok'
    }
    case 'soundcloud':  return 'SoundCloud'
    case 'bandcamp':    return 'Bandcamp'
    case 'apple_music': return 'Apple Music'
    case 'wikipedia':   return 'Wikipedia'
    default:            return 'website'
  }
}

// ── Lazy embed hook ───────────────────────────────────────────────────────
// Returns a ref to attach to the placeholder and a boolean that flips to true
// once the element enters the viewport. Never flips back — iframe stays mounted.

function useInViewOnce(rootMargin = '200px') {
  const ref = useRef<HTMLDivElement | null>(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    if (inView) return
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true) },
      { rootMargin }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [inView, rootMargin])
  return { ref, inView }
}

// ── Component ─────────────────────────────────────────────────────────────

interface CandidateCardProps {
  candidate: Candidate
  onApprove?: () => Promise<void>
  onSkip?: () => Promise<void>
  onDelete?: () => Promise<void>
}

export default function CandidateCard({ candidate, onApprove, onSkip, onDelete }: CandidateCardProps) {
  const [approving, setApproving] = useState(false)
  const [skipping,  setSkipping]  = useState(false)
  const [deleting,  setDeleting]  = useState(false)
  const [done,      setDone]      = useState<'approved' | 'skipped' | 'deleted' | null>(null)
  const [error,     setError]     = useState<string | null>(null)

  const [links, setLinks] = useState<string[]>(() => parseLinks(candidate.links ?? ''))

  // Sync when the candidate prop is updated externally (e.g. agent edits an existing candidate)
  useEffect(() => {
    setLinks(parseLinks(candidate.links ?? ''))
  }, [candidate.links])

  const embed = findEmbed(links, candidate.type ?? 'Artist')
  const { ref: embedRef, inView: embedInView } = useInViewOnce()

  async function handleApprove() {
    setError(null); setApproving(true)
    try { await onApprove?.(); setDone('approved') }
    catch (e) { setError(e instanceof Error ? e.message : 'Approve failed') }
    finally   { setApproving(false) }
  }

  async function handleSkip() {
    setError(null); setSkipping(true)
    try { await onSkip?.(); setDone('skipped') }
    catch (e) { setError(e instanceof Error ? e.message : 'Skip failed') }
    finally   { setSkipping(false) }
  }

  async function handleDelete() {
    setError(null); setDeleting(true)
    try { await onDelete?.(); setDone('deleted') }
    catch (e) { setError(e instanceof Error ? e.message : 'Delete failed') }
    finally   { setDeleting(false) }
  }

  if (done) {
    return (
      <div style={{ ...cardStyle, opacity: 0.4, display: 'flex', alignItems: 'center', gap: '8px', padding: '14px 16px' }}>
        {done === 'approved'
          ? <CheckIcon  style={{ width: 16, color: 'var(--success)' }} />
          : done === 'deleted'
          ? <TrashIcon  style={{ width: 16, color: 'var(--text-muted)' }} />
          : <XMarkIcon  style={{ width: 16, color: 'var(--danger)'  }} />}
        <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
          <strong>{candidate.label}</strong> — {done}
        </span>
      </div>
    )
  }

  return (
    <div style={cardStyle}>
      {/* ── Header ── */}
      <div style={{ padding: '14px 16px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: '15px', color: 'var(--accent)' }}>
              {candidate.label}
            </span>
            {candidate.score && (
              <span style={badgeStyle}>score {candidate.score}</span>
            )}
          </div>
          {(candidate.genre || candidate.location) && (
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '3px' }}>
              {[candidate.genre, candidate.location].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>

        {/* Action buttons — hidden in read-only (no handlers provided) */}
        {(onApprove || onSkip || onDelete) && (
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0, alignItems: 'center' }}>
            {(approving || skipping || deleting) ? (
              <div style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{
                  width: 16, height: 16, borderRadius: '50%',
                  border: '2px solid var(--border)',
                  borderTopColor: approving ? 'var(--success)' : deleting ? 'var(--text-muted)' : 'var(--danger)',
                  animation: 'spin 0.7s linear infinite',
                }} />
              </div>
            ) : (
              <>
                {onDelete && (
                  <button onClick={handleDelete} title="Delete (bad/duplicate)" style={actionBtn('muted')}>
                    <TrashIcon style={{ width: 16 }} />
                  </button>
                )}
                {onSkip && (
                  <button onClick={handleSkip} title="Skip" style={actionBtn('danger')}>
                    <XMarkIcon style={{ width: 18 }} />
                  </button>
                )}
                {onApprove && (
                  <button onClick={handleApprove} title="Add to network" style={actionBtn('success')}>
                    <CheckIcon style={{ width: 18 }} />
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Description ── */}
      {candidate.description && (
        <div style={{ padding: '0 16px 10px', fontSize: '13px', color: 'var(--text-muted)', lineHeight: 1.5 }}>
          {candidate.description}
        </div>
      )}

      {/* ── Embed (one max, first available in priority order) ── */}
      {embed && (embed.type === 'spotify' || embed.type === 'apple_music' || embed.type === 'soundcloud') && (
        <div ref={embedRef} style={{ padding: '0 16px 10px' }}>
          {embedInView && embed.type === 'spotify' && (
            <iframe
              title={`Spotify — ${candidate.label}`}
              src={`https://open.spotify.com/embed/artist/${embed.spotifyId}?utm_source=generator&theme=0`}
              width="100%" height="152" frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              style={{ borderRadius: '8px', display: 'block' }}
            />
          )}
          {embedInView && embed.type === 'apple_music' && (
            <iframe
              title={`Apple Music — ${candidate.label}`}
              src={embed.embedUrl}
              width="100%" height="175" frameBorder="0"
              allow="autoplay *; encrypted-media *; fullscreen *"
              style={{ borderRadius: '10px', display: 'block', background: 'transparent' }}
            />
          )}
          {embedInView && embed.type === 'soundcloud' && (
            <iframe
              title={`SoundCloud — ${candidate.label}`}
              width="100%" height="120" scrolling="no" frameBorder="no" allow="autoplay"
              src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(embed.profileUrl)}&color=%23606060&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false`}
              style={{ borderRadius: '8px', display: 'block' }}
            />
          )}
          {!embedInView && (
            <div style={{
              height: embed.type === 'apple_music' ? 175 : embed.type === 'spotify' ? 152 : 120,
              borderRadius: '8px',
              background: 'rgba(255,255,255,0.04)',
            }} />
          )}
        </div>
      )}

      {embed?.type === 'instagram' && (
        <div style={{ padding: '0 16px 10px' }}>
          <a
            href={embed.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '10px 14px',
              background: 'rgba(255,255,255,0.06)',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              color: 'var(--text-muted)',
              textDecoration: 'none',
              fontSize: '13px',
            }}
          >
            <FontAwesomeIcon icon={faInstagram} style={{ fontSize: '16px' }} />
            <span>{embed.url.replace(/^https?:\/\/(www\.)?instagram\.com\//, '@').replace(/\/$/, '')}</span>
          </a>
        </div>
      )}

      {/* ── Links row ── */}
      <div style={{ padding: '0 16px 14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {links.length > 0 && (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
            {links.map((url, i) => {
              const platform = detectPlatform(url)
              return (
                <SocialLink
                  key={i}
                  href={url}
                  icon={platformIcon(platform)}
                  label={platformLabel(platform, url)}
                />
              )
            })}
          </div>
        )}

        {/* Source notes */}
        {candidate.source_notes && (
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', opacity: 0.65 }}>
            {candidate.source_notes}
          </div>
        )}

        {error && (
          <div style={{ fontSize: '12px', color: 'var(--danger)', marginTop: '2px' }}>⚠ {error}</div>
        )}
      </div>
    </div>
  )
}

// ── Sub-components ─────────────────────────────────────────────────────────

function SocialLink({ href, icon, label }: { href: string; icon: React.ReactNode; label: string }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" style={socialLinkStyle}>
      <span style={{ fontSize: '11px', lineHeight: 1 }}>{icon}</span>
      {label}
    </a>
  )
}

// ── Styles ─────────────────────────────────────────────────────────────────

const cardStyle: CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: '10px',
  overflow: 'hidden',
}

const badgeStyle: CSSProperties = {
  fontSize: '11px', fontWeight: 600,
  background: 'rgba(255,255,255,0.08)',
  color: 'var(--text-muted)',
  borderRadius: '10px', padding: '2px 7px',
}

function actionBtn(variant: 'danger' | 'success' | 'muted'): CSSProperties {
  const color = variant === 'danger' ? 'var(--danger)' : variant === 'success' ? 'var(--success)' : 'var(--text-muted)'
  const bg    = variant === 'danger' ? 'rgba(239,68,68,0.12)' : variant === 'success' ? 'rgba(134,239,172,0.12)' : 'rgba(255,255,255,0.06)'
  return {
    width: 36, height: 36, borderRadius: '50%',
    background: bg, color,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'background 0.15s',
  }
}

const socialLinkStyle: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '5px',
  fontSize: '11px', color: 'var(--text-muted)', textDecoration: 'none',
  background: 'rgba(255,255,255,0.06)', borderRadius: '10px', padding: '3px 8px',
}
